import { useEffect, useSyncExternalStore } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { reportClientVersion } from "@/lib/clientVersion/reportClientVersion";
import {
  getClientSignature,
  getClientSignatureParts,
  subscribeClientSignature,
} from "@/lib/clientVersion/signature";

/**
 * Monté une fois, DANS AuthProvider (App.tsx). Signale la version de l'app
 * pour le compte connecté, et de nouveau quand la signature change (sur natif,
 * la version native arrive après le démarrage). Ne rend rien.
 *
 * Sur natif, reportClientVersion ne signale rien tant que la version native
 * n'est pas lue, pour ne jamais inscrire la version du bundle à sa place. Si
 * cette lecture a échoué au démarrage, la signature ne change plus : on
 * réessaie donc aussi à chaque retour de l'app au premier plan. Un signalement
 * déjà fait n'est pas rejoué (reportClientVersion s'en souvient).
 */
export function ClientVersionReporter() {
  const { user } = useAuth();
  const signature = useSyncExternalStore(subscribeClientSignature, getClientSignature, getClientSignature);
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId) return;
    const report = () => void reportClientVersion(userId, getClientSignatureParts());
    report();
    if (getClientSignatureParts().platform === "web") return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") report();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [userId, signature]);

  return null;
}
